import { Room } from './room';
import { Player } from './player';
import { InputManager, LocalKeyboardSource, type InputAction } from './inputManager';
import type { TeamConfig } from '../logic/team';

export type SessionEventType =
  | 'playerJoined'
  | 'playerLeft'
  | 'teamClaimed'
  | 'teamReleased'
  | 'gameStarted'
  | 'turnChanged'
  | 'gameEnded';

export interface SessionEvent {
  type: SessionEventType;
  data: unknown;
}

export type SessionEventHandler = (event: SessionEvent) => void;

export class GameSession {
  private room: Room;
  private localPlayer: Player;
  private inputManager: InputManager;
  private localInput: LocalKeyboardSource;
  private eventHandlers: Set<SessionEventHandler> = new Set();

  constructor(room: Room, localPlayer: Player) {
    this.room = room;
    this.localPlayer = localPlayer;
    this.inputManager = new InputManager();
    this.localInput = new LocalKeyboardSource(localPlayer.id);
    this.inputManager.registerSource(this.localInput);
  }

  getRoom(): Room {
    return this.room;
  }

  getLocalPlayer(): Player {
    return this.localPlayer;
  }

  isLocalPlayerTurn(currentTeamId: string): boolean {
    return this.localPlayer.ownsTeam(currentTeamId);
  }

  canLocalPlayerControl(currentTeamId: string): boolean {
    if (this.room.state !== 'playing') return false;
    return this.isLocalPlayerTurn(currentTeamId);
  }

  claimTeam(teamId: string): boolean {
    const success = this.room.claimTeam(this.localPlayer.id, teamId);
    if (success) {
      this.emit({ type: 'teamClaimed', data: { playerId: this.localPlayer.id, teamId } });
    }
    return success;
  }

  releaseTeam(teamId: string): boolean {
    const success = this.room.releaseTeam(this.localPlayer.id, teamId);
    if (success) {
      this.emit({ type: 'teamReleased', data: { playerId: this.localPlayer.id, teamId } });
    }
    return success;
  }

  setMaxTeamsPerPlayer(value: number): void {
    this.room.setMaxTeamsPerPlayer(value);
  }

  setReady(ready: boolean): void {
    this.localPlayer.setReady(ready);
  }

  canStart(): boolean {
    return this.room.isHost(this.localPlayer.id) && this.room.canStart();
  }

  startGame(): boolean {
    if (!this.canStart()) return false;
    const success = this.room.start();
    if (success) {
      this.emit({ type: 'gameStarted', data: {} });
    }
    return success;
  }

  getActiveTeamConfigs(): TeamConfig[] {
    return this.room.getActiveTeamConfigs();
  }

  getTeamOwner(teamId: string): Player | null {
    return this.room.getTeamOwner(teamId);
  }

  pollLocalInput(): InputAction[] {
    return this.localInput.poll();
  }

  updateInputCharge(deltaTime: number): void {
    this.localInput.updateCharge(deltaTime);
  }

  isMovingLeft(): boolean {
    return this.localInput.isKeyDown('ArrowLeft') || this.localInput.isKeyDown('KeyA');
  }

  isMovingRight(): boolean {
    return this.localInput.isKeyDown('ArrowRight') || this.localInput.isKeyDown('KeyD');
  }

  isAimingUp(): boolean {
    return this.localInput.isKeyDown('ArrowUp') || this.localInput.isKeyDown('KeyW');
  }

  isAimingDown(): boolean {
    return this.localInput.isKeyDown('ArrowDown') || this.localInput.isKeyDown('KeyS');
  }

  onEvent(handler: SessionEventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }

  private emit(event: SessionEvent): void {
    for (const handler of this.eventHandlers) {
      handler(event);
    }
  }

  destroy(): void {
    this.localInput.destroy();
    this.eventHandlers.clear();
  }
}
